import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';

function esc(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[char]);
}

function toCents(value, stringIsMajor = false) {
  if (value == null || value === '') return 0;
  if (typeof value === 'string' && stringIsMajor) return Math.round(Number(value) * 100);
  return Number(value) || 0;
}

function money(cents) {
  const value = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'ro-RO', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'RON',
      maximumFractionDigits: 2,
    }).format(value);
  } catch (_) {
    return `${value.toFixed(2)} lei`;
  }
}

function productPriceCents(product, variant) {
  if (typeof product?.price === 'number') return toCents(product.price);
  if (variant?.price != null) return toCents(variant.price, typeof variant.price === 'string');
  return 0;
}

function productImage(product) {
  if (typeof product?.featured_image === 'string') return product.featured_image;
  return product?.featured_image?.url || product?.featured_image?.src || product?.images?.[0]?.src || '';
}

async function fetchRecommendations(productId, intent, limit = 6) {
  const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=${intent}`;
  const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data.products) ? data.products : [];
}

async function fetchCollectionProducts(handle, limit = 12) {
  if (!handle) return [];
  const url = `${root}collections/${encodeURIComponent(handle)}/products.json?limit=${Math.min(250, Math.max(limit, 12))}`;
  const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data.products) ? data.products : [];
}

async function addVariant(button, context = 'cart') {
  const variantId = Number(button.dataset.variantId);
  if (!variantId || button.disabled) return;
  button.disabled = true;
  const original = button.textContent;
  button.textContent = 'Se adaugă…';

  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context,
    lines: [{ merchandiseId: String(variantId), quantity: 1 }],
    promise: deferred.promise,
  }));

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add to cart failed');

    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'arway-cross-sell',
        sourceId: button.closest('[data-arway-cross-sell-root]')?.id || 'arway-cross-sell',
        itemCount: 1,
        productId: button.dataset.productId,
        sections: result.sections,
        didError: false,
      },
    });
    button.textContent = 'Adăugat ✓';
  } catch (error) {
    deferred.reject(error);
    button.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Nu am putut adăuga produsul', code: 'INVALID' }));
    button.disabled = false;
    button.textContent = original;
  }
}

class ArwayProductSavings extends HTMLElement {
  connectedCallback() {
    this.variants = [];
    try {
      this.variants = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '[]');
    } catch (_) {}

    const currentId = Number(this.dataset.currentVariantId || 0);
    this.render(this.variants.find((item) => Number(item.id) === currentId) || this.variants[0]);

    this.eventRoot = this.closest('.shopify-section');
    this.onProductSelect = (event) => {
      if (!event?.promise) return;
      event.promise.then(({ detail }) => {
        if (detail?.resource) this.render(detail.resource);
      }).catch(() => {});
    };
    this.eventRoot?.addEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  disconnectedCallback() {
    this.eventRoot?.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  render(variant) {
    const price = Number(variant?.price || 0);
    const compare = Number(variant?.compare_at_price || 0);
    if (!compare || compare <= price) {
      this.hidden = true;
      this.innerHTML = this.querySelector('script[type="application/json"]')?.outerHTML || '';
      return;
    }
    const saved = compare - price;
    const percent = Math.round((saved / compare) * 100);
    this.hidden = false;
    this.innerHTML = `<span>Economisești</span><strong>${money(saved)}</strong><span class="arway-product-savings__percent">−${percent}%</span>`;
  }
}

class ArwayCartCrossSell extends HTMLElement {
  loadedFor = null;

  connectedCallback() {
    this.load();
  }

  async load() {
    const productId = this.dataset.productId;
    if (!productId || this.loadedFor === productId) return;
    this.loadedFor = productId;
    this.setAttribute('aria-busy', 'true');

    try {
      const signal = (this.dataset.productSignal || '').toLowerCase();
      const accessoryHandle = this.dataset.accessoryCollection || '';
      let products = [];

      if (accessoryHandle && /(atv|quad|motocross|cross|dirt|pit\s?bike)/i.test(signal)) {
        products = await fetchCollectionProducts(accessoryHandle, 8);
        products = products.filter((product) => String(product.id) !== String(productId));
      }
      if (!products.length) products = await fetchRecommendations(productId, 'complementary', 6);
      if (!products.length) products = await fetchRecommendations(productId, 'related', 6);
      this.render(products.slice(0, 3));
    } catch (error) {
      console.warn('[ARWAY] Cross-sell unavailable:', error);
      this.hidden = true;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  render(products) {
    if (!products.length) {
      this.hidden = true;
      return;
    }

    const cards = products.map((product) => {
      const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
      const image = productImage(product);
      const url = product.url || `${root}products/${product.handle}`;
      const price = productPriceCents(product, variant);
      const add = variant?.available
        ? `<button type="button" class="arway-cross-sell__add" data-arway-cross-add data-variant-id="${variant.id}" data-product-id="${product.id}">Adaugă</button>`
        : `<a class="arway-cross-sell__add arway-cross-sell__add--link" href="${url}">Vezi</a>`;

      return `<article class="arway-cross-sell__card">
        <a class="arway-cross-sell__media" href="${url}" aria-label="${esc(product.title)}">${image ? `<img src="${image}" alt="" loading="lazy" width="120" height="120">` : ''}</a>
        <div class="arway-cross-sell__info">
          <a class="arway-cross-sell__title" href="${url}">${esc(product.title)}</a>
          <span class="arway-cross-sell__price">${money(price)}</span>
        </div>
        ${add}
      </article>`;
    }).join('');

    this.hidden = false;
    this.innerHTML = `<div class="arway-cross-sell__head"><strong>Echipamentul face diferența</strong><span>Accesorii recomandate pentru vehiculul din coș</span></div><div class="arway-cross-sell__list">${cards}</div>`;
    this.querySelectorAll('[data-arway-cross-add]').forEach((button) => button.addEventListener('click', () => addVariant(button, 'cart')));
  }
}

class ArwayProductCrossSell extends HTMLElement {
  connectedCallback() {
    this.load();
  }

  async load() {
    if (this.dataset.loaded === 'true') return;
    this.dataset.loaded = 'true';
    const productId = this.dataset.productId;
    const handle = this.dataset.collectionHandle || '';
    const limit = Math.min(6, Math.max(2, Number(this.dataset.limit || 4)));
    this.setAttribute('aria-busy', 'true');

    try {
      let products = handle ? await fetchCollectionProducts(handle, Math.max(limit * 2, 8)) : [];
      products = products.filter((product) => String(product.id) !== String(productId));
      if (!products.length) products = await fetchRecommendations(productId, 'complementary', limit * 2);
      if (!products.length) products = await fetchRecommendations(productId, 'related', limit * 2);
      this.render(products.slice(0, limit));
    } catch (error) {
      console.warn('[ARWAY] Product cross-sell unavailable:', error);
      this.closest('.arway-contextual')?.setAttribute('hidden', '');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  render(products) {
    if (!products.length) {
      this.closest('.arway-contextual')?.setAttribute('hidden', '');
      return;
    }

    this.innerHTML = products.map((product) => {
      const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
      const image = productImage(product);
      const url = product.url || `${root}products/${product.handle}`;
      const price = productPriceCents(product, variant);
      const compareRaw = variant?.compare_at_price;
      const compare = compareRaw ? toCents(compareRaw, typeof compareRaw === 'string') : 0;
      const priceHtml = compare > price
        ? `<span class="arway-contextual__compare">${money(compare)}</span><strong>${money(price)}</strong>`
        : `<strong>${money(price)}</strong>`;
      const action = variant?.available
        ? `<button type="button" class="arway-contextual__add" data-arway-cross-add data-variant-id="${variant.id}" data-product-id="${product.id}">Adaugă</button>`
        : `<a class="arway-contextual__add arway-contextual__add--link" href="${url}">Vezi produsul</a>`;
      return `<article class="arway-contextual__card">
        <a href="${url}" class="arway-contextual__media" aria-label="${esc(product.title)}">${image ? `<img src="${image}" alt="" loading="lazy">` : ''}</a>
        <div class="arway-contextual__body">
          <a href="${url}" class="arway-contextual__title">${esc(product.title)}</a>
          <div class="arway-contextual__price">${priceHtml}</div>
          ${action}
        </div>
      </article>`;
    }).join('');

    this.querySelectorAll('[data-arway-cross-add]').forEach((button) => button.addEventListener('click', () => addVariant(button, 'product')));
  }
}

if (!customElements.get('arway-product-savings')) customElements.define('arway-product-savings', ArwayProductSavings);
if (!customElements.get('arway-cart-cross-sell')) customElements.define('arway-cart-cross-sell', ArwayCartCrossSell);
if (!customElements.get('arway-product-cross-sell')) customElements.define('arway-product-cross-sell', ArwayProductCrossSell);

// ARWAY v10 — finance estimate, delivery estimate, package builder and model comparison.
function currentProductVariantId(productId, fallback) {
  const selector = productId
    ? `product-form-component[data-product-id="${CSS.escape(String(productId))}"] input[name="id"]`
    : 'product-form-component input[name="id"]';
  const input = document.querySelector(selector);
  return Number(input?.value || fallback || 0);
}

async function addItemsToCart(button, items, context = 'arway-package') {
  const cleanItems = items
    .map((item) => ({ id: Number(item.id), quantity: Math.max(1, Number(item.quantity || 1)) }))
    .filter((item) => item.id);
  if (!cleanItems.length) return false;
  if (button instanceof HTMLElement) delete button.dataset.arwayCartError;

  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  const emitter = button || document.body;
  emitter.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context,
    lines: cleanItems.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
    promise: deferred.promise,
  }));

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: cleanItems,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add to cart failed');

    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: context,
        sourceId: 'arway-package-builder',
        itemCount: cleanItems.reduce((sum, item) => sum + item.quantity, 0),
        sections: result.sections,
        didError: false,
      },
    });
    return true;
  } catch (error) {
    deferred.reject(error);
    const message = error?.message || 'Nu am putut adăuga pachetul';
    if (button instanceof HTMLElement) button.dataset.arwayCartError = message;
    emitter.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
    return false;
  }
}

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function productSignal(product) {
  return normalizeText([
    product?.title,
    product?.type,
    Array.isArray(product?.tags) ? product.tags.join(' ') : product?.tags,
    String(product?.description || '').replace(/<[^>]+>/g, ' '),
  ].join(' '));
}

function extractProductSpecs(product) {
  const signal = productSignal(product);
  const map = {};
  try {
    const doc = new DOMParser().parseFromString(String(product?.description || product?.body_html || ''), 'text/html');
    doc.querySelectorAll('[data-arway-spec-key]').forEach((row) => {
      const key = row.getAttribute('data-arway-spec-key');
      const cells = row.querySelectorAll('td,th');
      if (key && cells.length > 1) map[key] = cells[cells.length - 1].textContent.trim();
    });
  } catch (_) {}
  const type = String(product?.type || product?.product_type || '—');
  const vehicle = normalizeText(type).includes('motocross') ? 'Motocross' : normalizeText(type).includes('utv') ? 'UTV / Buggy' : normalizeText(type).includes('atv') ? 'ATV' : type;
  const fallbackFuel = /\belectric/.test(signal) ? 'Electric' : /benzina|petrol|gasoline/.test(signal) ? 'Benzină' : '—';
  return {
    category: vehicle, fuel: map.fuel || fallbackFuel, engine: map.engine || '—', power: map.power || '—', cooling: map.cooling || '—', fuel_supply: map.fuel_supply || '—', start: map.start || '—', transmission: map.transmission || '—', reverse: map.reverse || '—', drive: map.drive || '—', brakes: map.brakes || '—', wheels: map.wheels || '—', speed: map.speed || '—', tank: map.tank || '—', battery: map.battery || '—', dimensions: map.dimensions || '—', seat_height: map.seat_height || '—', weight: map.weight || '—', max_load: map.max_load || '—', user: map.user || '—', homologation: map.homologation || '—', availability: product?.available === false ? 'Indisponibil' : 'Disponibil'
  };
}

async function fetchProductByHandle(handle) {
  const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Product ${handle} unavailable`);
  return response.json();
}

class ArwayProductConversion extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.querySelector('[data-arway-conversion-variants]')?.textContent || '[]');
    } catch (_) {
      this.variants = [];
    }
    this.months = this.querySelector('[data-arway-finance-months]');
    this.rate = this.querySelector('[data-arway-monthly-rate]');
    this.currentVariantId = Number(this.dataset.currentVariantId || 0);
    this.renderRate();
    this.months?.addEventListener('change', () => this.renderRate());

    this.eventRoot = this.closest('.shopify-section');
    this.onProductSelect = (event) => {
      if (!event?.promise) return;
      event.promise.then(({ detail }) => {
        const resource = detail?.resource;
        if (resource?.id) {
          this.currentVariantId = Number(resource.id);
          this.renderRate(resource);
        }
      }).catch(() => {});
    };
    this.eventRoot?.addEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  disconnectedCallback() {
    this.eventRoot?.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
  }

  renderRate(variantOverride) {
    const id = currentProductVariantId(null, this.currentVariantId);
    const variant = variantOverride || this.variants.find((item) => Number(item.id) === Number(id)) || this.variants[0];
    if (!variant || !this.rate) return;
    const months = Math.max(1, Number(this.months?.value || 60));
    const monthly = Math.ceil(Number(variant.price || 0) / months);
    this.rate.textContent = `de la ~${money(monthly)} / lună`;
  }
}

class ArwayDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-arway-delivery-region]');
    this.result = this.querySelector('[data-arway-delivery-result]');
    this.select?.addEventListener('change', () => this.render());
  }

  render() {
    if (!this.result) return;
    if (this.select?.value === 'arad') this.result.textContent = `${Number(this.dataset.aradPrice || 150)} lei`;
    else if (this.select?.value === 'romania') this.result.textContent = `~${money(Number(this.dataset.standardPrice || 500) * 100)}`;
    else this.result.textContent = '—';
  }
}

class ArwayPackageBuilder extends HTMLElement {
  loaded = false;
  targetAge() {
    const s = normalizeText(this.dataset.userRange || this.dataset.public || '');
    const nums = [...s.matchAll(/\d+/g)].map((m) => Number(m[0]));
    if (nums.length >= 2) return (nums[0] + nums[1]) / 2;
    if (nums.length === 1) return nums[0];
    return /adult/.test(s) ? 18 : /copii|adolescent/.test(s) ? 9 : 12;
  }
  gearKind(product) {
    const s = productSignal(product);
    if (/casca|helmet/.test(s)) return 'helmet';
    if (/manusi|glove/.test(s)) return 'gloves';
    if (/geaca|bluza|jersey|costum|protectie/.test(s)) return 'apparel';
    return 'other';
  }
  ageScore(product, age) {
    const s = productSignal(product); let score = 0;
    const nums = [...s.matchAll(/(\d{1,2})\s*(?:-|–|—|pana la|până la)\s*(\d{1,2})\s*ani/g)].map((m) => [Number(m[1]), Number(m[2])]);
    nums.forEach(([a,b]) => { if (age >= a-1 && age <= b+1) score += 12; else score -= Math.min(8, Math.abs(age-(a+b)/2)); });
    if (age < 13 && /copii/.test(s)) score += 5;
    if (age >= 15 && /copii/.test(s)) score -= 7;
    if (/universal/.test(s)) score += 1;
    return score;
  }
  async load() {
    if (this.loaded) return; this.loaded = true;
    try { this.variants = JSON.parse(this.querySelector('[data-arway-package-variants]')?.textContent || '[]'); } catch (_) { this.variants = []; }
    this.setAttribute('aria-busy','true');
    try {
      const handles=[this.dataset.collectionHandle,'echipamente-accesorii','echipamente','accesorii'].filter(Boolean);
      let products=[];
      for (const h of [...new Set(handles)]) {
        try {
          const batch=await fetchCollectionProducts(h,120);
          if(batch.length) products.push(...batch);
        } catch(_){}
      }
      // Collection handles can be renamed in Shopify. The catalog is small enough that
      // /collections/all is a safe fallback, then we keep only actual protective gear.
      const presentKinds=new Set(products.map((x)=>this.gearKind(x)).filter((x)=>x!=='other'));
      if(products.length<3 || presentKinds.size<3){
        try { products.push(...await fetchCollectionProducts('all',250)); } catch(_){}
      }
      products=[...new Map(products.filter((x)=>this.gearKind(x)!=='other').map((x)=>[x.id,x])).values()];
      const age=this.targetAge();
      const chosen=[];
      ['helmet','gloves','apparel'].forEach((kind)=>{
        const candidate=products.filter((x)=>this.gearKind(x)===kind).sort((a,b)=>this.ageScore(b,age)-this.ageScore(a,age))[0];
        if(candidate && !chosen.some((x)=>x.id===candidate.id)) chosen.push(candidate);
      });
      if(chosen.length<3) products.sort((a,b)=>this.ageScore(b,age)-this.ageScore(a,age)).forEach((x)=>{if(chosen.length<3&&!chosen.some((c)=>c.id===x.id))chosen.push(x)});
      this.render(chosen.slice(0,3),age);
    } catch(error){ console.warn('[ARWAY] Package builder unavailable:',error); this.innerHTML='<div class="arway-package-builder__empty">Nu am putut încărca echipamentele.</div>'; }
    finally { this.removeAttribute('aria-busy'); }
  }
  render(products, age) {
    if(!products.length){this.innerHTML='<div class="arway-package-builder__empty">Nu există încă echipamente recomandate.</div>';return;}
    const cards=products.map((product)=>{const variant=product.variants?.find((v)=>v.available)||product.variants?.[0];if(!variant)return'';const image=productImage(product),price=productPriceCents(product,variant),url=product.url||`${root}products/${product.handle}`;return `<label class="arway-package-card"><input type="checkbox" data-arway-package-item value="${esc(variant.id)}" data-price="${price}" ${variant.available?'checked':'disabled'}><span class="arway-package-card__media">${image?`<img src="${image}" alt="" loading="lazy" width="160" height="160">`:''}</span><span class="arway-package-card__copy"><strong>${esc(product.title)}</strong><small>${money(price)}</small><a href="${url}">Detalii</a></span></label>`}).join('');
    this.innerHTML=`<div class="arway-package-builder__match"><strong>Recomandare pentru ${esc(this.dataset.userRange||this.dataset.public||'utilizator')}</strong><span>Cască + mănuși + echipament, selectate după categoria și vârsta orientativă a vehiculului.</span></div><div class="arway-package-builder__vehicle"><label><input type="checkbox" data-arway-package-vehicle checked> Include și vehiculul</label><span>Debifează dacă este deja în coș.</span></div><div class="arway-package-builder__grid">${cards}</div><div class="arway-package-builder__footer"><div><span data-arway-package-status>Pachet recomandat selectat.</span><strong data-arway-package-total>—</strong></div><button type="button" data-arway-package-add>Adaugă pachetul în coș</button></div>`;
    this.querySelectorAll('[data-arway-package-item],[data-arway-package-vehicle]').forEach((x)=>x.addEventListener('change',()=>this.updateTotal())); this.querySelector('[data-arway-package-add]')?.addEventListener('click',(e)=>this.addPackage(e.currentTarget)); this.updateTotal();
  }
  updateTotal(){let total=0;if(this.querySelector('[data-arway-package-vehicle]')?.checked){const id=currentProductVariantId(this.dataset.productId,this.dataset.currentVariantId);const v=this.variants?.find((x)=>Number(x.id)===Number(id))||this.variants?.[0];total+=Number(v?.price||0)}this.querySelectorAll('[data-arway-package-item]:checked').forEach((x)=>total+=Number(x.dataset.price||0));const out=this.querySelector('[data-arway-package-total]');if(out)out.textContent=total?`Total estimat: ${money(total)}`:'—'}
  async addPackage(button){
    const items=[];
    const status=this.querySelector('[data-arway-package-status]');
    if(this.querySelector('[data-arway-package-vehicle]')?.checked){
      const id=currentProductVariantId(this.dataset.productId,this.dataset.currentVariantId);
      const vehicleVariant=this.variants?.find((x)=>Number(x.id)===Number(id));
      if(id && (!vehicleVariant || vehicleVariant.available !== false)) items.push({id,quantity:1});
      else if(status) status.textContent='Varianta vehiculului selectată nu este disponibilă.';
    }
    this.querySelectorAll('[data-arway-package-item]:checked').forEach((x)=>items.push({id:Number(x.value),quantity:1}));
    if(!items.length){if(status)status.textContent='Selectează cel puțin un produs.';return;}
    const original=button.textContent;
    button.disabled=true;
    button.textContent='Se adaugă…';
    const ok=await addItemsToCart(button,items,'arway-package');
    if(ok){
      button.textContent='Pachet adăugat ✓';
      if(status)status.textContent='Produsele sunt în coș.';
      setTimeout(()=>this.closest('dialog')?.close(),900);
    }else{
      button.disabled=false;
      button.textContent=original;
      if(status)status.textContent=button.dataset.arwayCartError||'Nu am putut adăuga pachetul.';
    }
  }
}

class ArwayCompareController {
  constructor() {
    this.key = 'arway_compare_models_v1';
    this.bar = document.querySelector('[data-arway-compare-bar]');
    this.dialog = document.querySelector('[data-arway-compare-dialog]');
    this.body = document.querySelector('[data-arway-compare-body]');
    this.handles = this.read();
    this.bind();
    this.renderState();
  }

  read() {
    try {
      const value = JSON.parse(localStorage.getItem(this.key) || '[]');
      return Array.isArray(value) ? value.filter(Boolean).slice(0, 3) : [];
    } catch (_) {
      return [];
    }
  }

  save() {
    try { localStorage.setItem(this.key, JSON.stringify(this.handles)); } catch (_) {}
  }

  bind() {
    document.addEventListener('click', (event) => {
      const add = event.target.closest('[data-arway-compare-card], [data-arway-compare-current]');
      if (add) {
        event.preventDefault();
        event.stopPropagation();
        const handle = add.dataset.productHandle;
        if (handle) this.toggle(handle);
        return;
      }
      const remove = event.target.closest('[data-arway-compare-remove]');
      if (remove) {
        this.remove(remove.dataset.arwayCompareRemove);
        return;
      }
      if (event.target.closest('[data-arway-compare-open]')) {
        event.preventDefault();
        this.open();
        return;
      }
      if (event.target.closest('[data-arway-compare-clear]')) {
        event.preventDefault();
        this.clear();
      }
    });
    document.querySelector('[data-arway-compare-close]')?.addEventListener('click', () => this.dialog?.close());
    this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); });
  }

  toggle(handle) {
    if (this.handles.includes(handle)) this.handles = this.handles.filter((item) => item !== handle);
    else if (this.handles.length < 3) this.handles.push(handle);
    else {
      this.bar?.animate?.([{ transform: 'translateY(0)' }, { transform: 'translateY(-5px)' }, { transform: 'translateY(0)' }], { duration: 220 });
      return;
    }
    this.save();
    this.renderState();
  }

  remove(handle) {
    this.handles = this.handles.filter((item) => item !== handle);
    this.save();
    this.renderState();
    if (this.dialog?.open) this.open(true);
  }

  clear() {
    this.handles = [];
    this.save();
    this.renderState();
    if (this.dialog?.open) this.dialog.close();
  }

  renderState() {
    const count = this.handles.length;
    if (this.bar) this.bar.hidden = count === 0;
    document.querySelectorAll('[data-arway-compare-count]').forEach((countEl) => {
      if (countEl.classList.contains('arway-header-compare__count')) countEl.textContent = String(count);
      else countEl.textContent = `${count} ${count === 1 ? 'model' : 'modele'}`;
    });
    document.querySelectorAll('[data-arway-compare-card], [data-arway-compare-current]').forEach((button) => {
      const selected = this.handles.includes(button.dataset.productHandle);
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
      if (button.matches('[data-arway-compare-card]')) {
        const icon = selected
          ? '<span class="arway-compare-card-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false"><circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.9"/><path d="m8.2 12.1 2.4 2.4 5.2-5.3" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg></span>'
          : '<span class="arway-compare-card-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false"><path d="M7 7h13m0 0-3-3m3 3-3 3M17 17H4m0 0 3 3m-3-3 3-3" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
        button.innerHTML = icon;
        button.setAttribute('aria-label', selected ? 'Scoate din comparație' : 'Adaugă la comparație');
        button.setAttribute('title', selected ? 'Selectat pentru comparație' : 'Compară produsul');
      }
      else button.textContent = selected ? 'Scoate din comparație' : 'Compară modelul';
    });
  }

  async open(refresh = false) {
    if (!this.dialog || !this.body) return;
    if (!refresh && typeof this.dialog.showModal === 'function' && !this.dialog.open) this.dialog.showModal();
    let handles=[...this.handles], example=false;
    const examples=['atv-125cc-kxd-defender-002-pornire-e-start','atv-xtl-ares-1000w-48v-brushless-portocaliu','motocross-125cc-xtr-607m-negru-rosu'];
    if(handles.length<2){example=true;for(const h of examples){if(!handles.includes(h))handles.push(h);if(handles.length>=2)break}}
    this.body.innerHTML='<div class="arway-compare-dialog__loading">Se încarcă modelele…</div>';
    try { const products=await Promise.all(handles.slice(0,3).map(fetchProductByHandle)); this.renderTable(products,example); }
    catch(error){console.warn('[ARWAY] Compare unavailable:',error);this.body.innerHTML='<div class="arway-compare-dialog__empty">Nu am putut încărca modelele. Reîncearcă.</div>'}
  }
  renderTable(products, example=false) {
    const columns=products.map((product)=>({product,specs:extractProductSpecs(product),image:productImage(product),url:`${root}products/${product.handle}`}));
    const note=example?'<div class="arway-compare-example-note">Exemplu de comparație — selectează modelele tale pentru a-l înlocui.</div>':'';
    const header=columns.map(({product,image,url})=>`<div class="arway-compare-model">${example?'':`<button type="button" data-arway-compare-remove="${esc(product.handle)}" aria-label="Scoate ${esc(product.title)}">×</button>`}<a href="${url}">${image?`<img src="${image}" alt="" loading="lazy" width="220" height="180">`:''}<strong>${esc(product.title)}</strong></a><span>${money(product.price||product.price_min||0)}</span></div>`).join('');
    const specRows=[['Categorie','category'],['Combustibil','fuel'],['Motor','engine'],['Putere','power'],['Răcire','cooling'],['Alimentare','fuel_supply'],['Pornire','start'],['Transmisie','transmission'],['Marșarier','reverse'],['Tracțiune','drive'],['Frâne','brakes'],['Roți','wheels'],['Viteză maximă','speed'],['Rezervor','tank'],['Baterie','battery'],['Dimensiuni','dimensions'],['Înălțime șa','seat_height'],['Greutate','weight'],['Sarcină maximă','max_load'],['Utilizator','user'],['Omologare','homologation'],['Disponibilitate','availability']];
    const rows=specRows.map(([label,key])=>`<div class="arway-compare-row"><strong>${label}</strong>${columns.map(({specs})=>`<span>${esc(specs[key]||'—')}</span>`).join('')}</div>`).join('');
    const actions=`<div class="arway-compare-row arway-compare-row--actions"><strong></strong>${columns.map(({url})=>`<span><a href="${url}">Vezi produsul</a></span>`).join('')}</div>`;
    this.body.innerHTML=`${note}<div class="arway-compare-grid" style="--arway-compare-cols:${columns.length}"><div class="arway-compare-grid__models"><div></div>${header}</div>${rows}${actions}</div>`;
  }
}

class ArwayWishlistController {
  constructor() {
    this.dialog = document.querySelector('[data-arway-wishlist-dialog]');
    this.body = document.querySelector('[data-arway-wishlist-body]');
    this.page = document.querySelector('[data-arway-wishlist-page]');
    this.pageBody = document.querySelector('[data-arway-wishlist-page-body]');
    this.customerKey = this.page?.dataset.customerKey || this.dialog?.dataset.customerKey || 'guest';
    this.key = `arway_wishlist_v1:${this.customerKey}`;
    this.migrate();
    this.handles = this.read();
    this.sharedHandles = this.readShared();
    this.bind();
    this.renderState();
    if (this.page) this.renderPage();
  }

  read() {
    try {
      const value = JSON.parse(localStorage.getItem(this.key) || '[]');
      return Array.isArray(value) ? [...new Set(value.filter(Boolean))].slice(0, 80) : [];
    } catch (_) { return []; }
  }

  readShared() {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('wishlist') || '';
      if (!raw) return [];
      return [...new Set(raw.split(',').map((x) => decodeURIComponent(x.trim())).filter((x) => /^[a-z0-9][a-z0-9-]{1,120}$/i.test(x)))].slice(0, 80);
    } catch (_) { return []; }
  }

  save() {
    try { localStorage.setItem(this.key, JSON.stringify(this.handles)); } catch (_) {}
  }

  migrate() {
    if (this.customerKey === 'guest') return;
    try {
      const guest = JSON.parse(localStorage.getItem('arway_wishlist_v1:guest') || '[]');
      const own = JSON.parse(localStorage.getItem(this.key) || '[]');
      const merged = [...new Set([...(Array.isArray(own) ? own : []), ...(Array.isArray(guest) ? guest : [])])];
      localStorage.setItem(this.key, JSON.stringify(merged));
      localStorage.removeItem('arway_wishlist_v1:guest');
    } catch (_) {}
  }

  activePageHandles() { return this.sharedHandles.length ? this.sharedHandles : this.handles; }

  bind() {
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-arway-wishlist-toggle]');
      if (toggle) {
        event.preventDefault();
        event.stopPropagation();
        this.toggle(toggle.dataset.productHandle);
        return;
      }
      if (event.target.closest('[data-arway-wishlist-open]')) {
        event.preventDefault();
        this.open();
        return;
      }
      const remove = event.target.closest('[data-arway-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        const handle = remove.dataset.arwayWishlistRemove;
        if (this.sharedHandles.length) {
          this.sharedHandles = this.sharedHandles.filter((h) => h !== handle);
          this.updateSharedUrl(false);
          this.renderPage();
        } else {
          this.toggle(handle);
          if (this.dialog?.open) this.open(true);
        }
        return;
      }
      const add = event.target.closest('[data-arway-wishlist-add]');
      if (add) {
        event.preventDefault();
        this.addWishlistProduct(add);
        return;
      }
      if (event.target.closest('[data-arway-wishlist-share]')) {
        event.preventDefault();
        this.share();
        return;
      }
      if (event.target.closest('[data-arway-wishlist-copy]')) {
        event.preventDefault();
        this.copyShareLink();
        return;
      }
      if (event.target.closest('[data-arway-wishlist-clear]')) {
        event.preventDefault();
        if (this.sharedHandles.length) {
          this.sharedHandles = [];
          history.replaceState({}, '', `${window.location.pathname}`);
        } else {
          this.handles = [];
          this.save();
        }
        this.renderState();
        this.renderPage();
        return;
      }
      if (event.target.closest('[data-arway-wishlist-save-shared]')) {
        event.preventDefault();
        this.handles = [...new Set([...this.handles, ...this.sharedHandles])].slice(0, 80);
        this.save();
        this.renderState();
        this.feedback('Lista partajată a fost salvată în lista ta.');
      }
    });
    document.querySelector('[data-arway-wishlist-close]')?.addEventListener('click', () => this.dialog?.close());
    this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); });
  }

  toggle(handle) {
    if (!handle) return;
    this.handles = this.handles.includes(handle) ? this.handles.filter((h) => h !== handle) : [...this.handles, handle];
    this.save();
    this.renderState();
    if (this.page && !this.sharedHandles.length) this.renderPage();
  }

  renderState() {
    document.querySelectorAll('[data-arway-wishlist-count]').forEach((x) => x.textContent = String(this.handles.length));
    document.querySelectorAll('[data-arway-wishlist-toggle]').forEach((button) => {
      const on = this.handles.includes(button.dataset.productHandle);
      button.classList.toggle('is-selected', on);
      button.setAttribute('aria-pressed', String(on));
      button.setAttribute('aria-label', on ? 'Scoate din lista de dorințe' : 'Adaugă în lista de dorințe');
      button.setAttribute('title', on ? 'Salvat în lista de dorințe' : 'Lista de dorințe');
      button.innerHTML = `<span aria-hidden="true">${on ? '♥' : '♡'}</span>`;
    });
  }

  shareLink() {
    const handles = this.activePageHandles();
    const base = `${window.location.origin}${root}pages/lista-de-dorinte`;
    return handles.length ? `${base}?wishlist=${handles.map(encodeURIComponent).join(',')}` : base;
  }

  updateSharedUrl(push = true) {
    if (!this.page) return;
    const link = this.shareLink();
    if (push) history.pushState({}, '', link);
    else history.replaceState({}, '', link);
  }

  feedback(message) {
    const el = document.querySelector('[data-arway-wishlist-share-feedback]');
    if (!el) return;
    el.textContent = message;
    window.clearTimeout(this.feedbackTimer);
    this.feedbackTimer = window.setTimeout(() => { el.textContent = ''; }, 3200);
  }

  async copyShareLink() {
    const link = this.shareLink();
    try {
      await navigator.clipboard.writeText(link);
      this.feedback('Link copiat. Îl poți trimite direct.');
    } catch (_) {
      window.prompt('Copiază linkul listei:', link);
    }
  }

  async share() {
    const link = this.shareLink();
    if (!this.activePageHandles().length) {
      this.feedback('Adaugă cel puțin un produs înainte să partajezi lista.');
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Lista mea ARWAY', text: 'Uite produsele ARWAY pe care le-am salvat.', url: link });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    await this.copyShareLink();
  }

  async addWishlistProduct(button) {
    const id = Number(button.dataset.variantId || 0);
    if (!id) return;
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Se adaugă…';
    const ok = await addItemsToCart(button, [{ id, quantity: 1 }], 'wishlist');
    button.textContent = ok ? 'Adăugat ✓' : original;
    button.disabled = !ok ? false : true;
  }

  async productsFor(handles) {
    const settled = await Promise.allSettled(handles.map(fetchProductByHandle));
    return settled.filter((x) => x.status === 'fulfilled').map((x) => x.value);
  }

  cardMarkup(product, shared = false) {
    const image = productImage(product);
    const url = `${root}products/${product.handle}`;
    const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
    const price = productPriceCents(product, variant);
    const removeLabel = shared ? 'Ascunde din lista partajată' : 'Elimină din lista de dorințe';
    return `<article class="arway-wishlist-page__card">
      <a class="arway-wishlist-page__card-media" href="${url}">${image ? `<img src="${image}" alt="${esc(product.title)}" loading="lazy" width="420" height="315">` : ''}</a>
      <div class="arway-wishlist-page__card-copy"><a href="${url}"><h2 class="arway-wishlist-page__card-title">${esc(product.title)}</h2></a><span class="arway-wishlist-page__card-price">${money(price)}</span></div>
      <div class="arway-wishlist-page__card-actions">
        <button type="button" class="arway-wishlist-page__add" data-arway-wishlist-add data-variant-id="${esc(variant?.id || '')}" ${variant?.available ? '' : 'disabled'}>${variant?.available ? 'Adaugă în coș' : 'Indisponibil'}</button>
        <button type="button" class="arway-wishlist-page__remove" data-arway-wishlist-remove="${esc(product.handle)}" aria-label="${removeLabel}" title="${removeLabel}">×</button>
      </div>
    </article>`;
  }

  async renderPage() {
    if (!this.page || !this.pageBody) return;
    const handles = this.activePageHandles();
    const shared = this.sharedHandles.length > 0;
    const notice = document.querySelector('[data-arway-wishlist-shared-notice]');
    const clear = document.querySelector('[data-arway-wishlist-clear]');
    const count = document.querySelector('[data-arway-wishlist-page-count]');
    if (notice) notice.hidden = !shared;
    if (clear) clear.textContent = shared ? 'Ascunde toate' : 'Golește lista';
    if (count) count.textContent = `${handles.length} ${handles.length === 1 ? 'produs' : 'produse'}`;
    if (!handles.length) {
      this.pageBody.innerHTML = `<div class="arway-wishlist-page__empty">Lista este goală.<br><a href="${root}collections/all">Vezi produsele</a></div>`;
      return;
    }
    this.pageBody.innerHTML = '<div class="arway-wishlist-page__empty">Se încarcă produsele…</div>';
    try {
      const products = await this.productsFor(handles);
      if (!products.length) throw new Error('No products');
      this.pageBody.innerHTML = `<div class="arway-wishlist-page__grid">${products.map((p) => this.cardMarkup(p, shared)).join('')}</div>`;
    } catch (_) {
      this.pageBody.innerHTML = '<div class="arway-wishlist-page__empty">Nu am putut încărca produsele din listă. Reîncearcă.</div>';
    }
  }

  async open(refresh = false) {
    if (!this.dialog || !this.body) {
      window.location.href = `${root}pages/lista-de-dorinte`;
      return;
    }
    if (!refresh && typeof this.dialog.showModal === 'function' && !this.dialog.open) this.dialog.showModal();
    if (!this.handles.length) {
      this.body.innerHTML = `<div class="arway-wishlist-empty">Wishlist-ul este gol. <a href="${root}collections/all">Vezi catalogul</a>.</div>`;
      return;
    }
    this.body.innerHTML = '<div class="arway-wishlist-empty">Se încarcă…</div>';
    try {
      const products = await this.productsFor(this.handles);
      this.body.innerHTML = `<div class="arway-wishlist-grid">${products.map((p) => {
        const img = productImage(p), url = `${root}products/${p.handle}`;
        return `<article><a href="${url}">${img ? `<img src="${img}" alt="${esc(p.title)}" loading="lazy">` : ''}<strong>${esc(p.title)}</strong><span>${money(p.price || 0)}</span></a><button type="button" data-arway-wishlist-remove="${esc(p.handle)}">Elimină</button></article>`;
      }).join('')}</div>`;
    } catch (_) { this.body.innerHTML = '<div class="arway-wishlist-empty">Nu am putut încărca wishlist-ul.</div>'; }
  }
}

function initArwayProductModals() {
  const testDialog = document.querySelector('[data-arway-test-drive-dialog]');
  const packageDialog = document.querySelector('[data-arway-package-dialog]');
  document.addEventListener('click', (event) => {
    const testOpen = event.target.closest('[data-arway-test-drive-open]');
    if (testOpen && testDialog) {
      event.preventDefault();
      if (typeof testDialog.showModal === 'function' && !testDialog.open) testDialog.showModal();
      return;
    }
    const packageOpen = event.target.closest('[data-arway-package-open]');
    if (packageOpen && packageDialog) {
      event.preventDefault();
      const builder = packageDialog.querySelector('arway-package-builder');
      builder?.load?.();
      if (typeof packageDialog.showModal === 'function' && !packageDialog.open) packageDialog.showModal();
    }
  });
  document.querySelector('[data-arway-test-drive-close]')?.addEventListener('click', () => testDialog?.close());
  document.querySelector('[data-arway-package-close]')?.addEventListener('click', () => packageDialog?.close());
  [testDialog, packageDialog].forEach((dialog) => dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); }));
  const date = document.querySelector('[data-arway-test-drive-date]');
  if (date && !date.min) { const now = new Date(); date.min = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`; }
  if (document.querySelector('[data-arway-test-drive-status]') && testDialog && typeof testDialog.showModal === 'function') testDialog.showModal();
}

function enhanceArwaySpecs() {
  document.querySelectorAll('[data-arway-specs]').forEach((section) => {
    const signal = normalizeText(section.dataset.productSignal || '');
    const pseudo = { title: signal, description: signal, type: section.querySelector('[data-arway-spec="category"]')?.textContent || '', tags: [] };
    const specs = extractProductSpecs(pseudo);
    ['engine', 'fuel', 'power', 'transmission'].forEach((key) => {
      const el = section.querySelector(`[data-arway-spec="${key}"]`);
      if (!el || el.textContent.trim() !== '—') return;
      const map = { engine: 'cc', fuel: 'fuel', power: 'power', transmission: 'transmission' };
      el.textContent = specs[map[key]] || '—';
    });
  });
}

if (!customElements.get('arway-product-conversion')) customElements.define('arway-product-conversion', ArwayProductConversion);
if (!customElements.get('arway-delivery-estimate')) customElements.define('arway-delivery-estimate', ArwayDeliveryEstimate);
if (!customElements.get('arway-package-builder')) customElements.define('arway-package-builder', ArwayPackageBuilder);

const bootArwayV10 = () => {
  if (!window.__arwayCompareController) window.__arwayCompareController = new ArwayCompareController();
  if (!window.__arwayWishlistController) window.__arwayWishlistController = new ArwayWishlistController();
  initArwayProductModals();
  enhanceArwaySpecs();
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootArwayV10, { once: true });
else bootArwayV10();
